/* eslint-disable import/no-cycle */
import { get } from 'lodash'
import { ThunkResult } from '../index'
import {
	FORM_TEMPLATE_QUEUES,
	FORM_TEMPLATE,
	FORM_TEMPLATE_DATABASES,
	FORM_TEMPLATE_PROCEDURES,
	FORM_TEMPLATE_COLUMNS,
	FORM_TEMPLATE_ELEMENTS,
	FORM_TEMPLATE_TABS,
	FORM_TEMPLATE_CONFIGS,
	FORM_TEMPLATE_ITEM_COPY,
	FORM_TABLE_ITEMS
} from './formTypes'

// utils
import { getReq } from '../../utils/request'

// types
import { IEmailConfig, IForm, IFormItemData, IItem, ITBaleFormItem } from '../../types/interfaces'

// enums
import { ELEMENT_ATTRIBUTES, PAGE } from '../../utils/enums'

export interface IFormTemplatePayload {
	data: IForm | null
}

export interface IQueuesPayload {
	data: any[] | null
}

export interface IDatabasesPayload {
	data: string[] | null
}

export interface IProceduresPayload {
	data: string[] | null
}

export interface IColumnsPayload {
	data: string[] | null
}

export interface IElementsPayload {
	data: string[] | null
}

export interface ITabsPayload {
	data: string[] | null
}

export interface IConfigsPayload {
	data: string[] | null
}

export interface IPlaceholderSize {
	height: number | null
	width: number | null
}

export interface IFormItemsPayload {
	data: {
		formElements: ITBaleFormItem[]
		callButtons: {
			label: string
			priority: number
		}[]
	} | null
}

export const getFormTemplate =
	(id?: string): ThunkResult<Promise<IFormTemplatePayload>> =>
	async (dispatch) => {
		try {
			dispatch({ type: FORM_TEMPLATE.FORM_TEMPLATE_LOAD_START })
			const { data } = await getReq(`/api/admin/forms/${id}`)

			const payload: any = {
				data: get(data, 'data.form')
			}

			dispatch({ type: FORM_TEMPLATE.FORM_TEMPLATE_LOAD_DONE, payload })
			return payload
		} catch (e) {
			dispatch({ type: FORM_TEMPLATE.FORM_TEMPLATE_LOAD_FAIL })
			return e
		}
	}

export const getQueues = (): ThunkResult<Promise<IQueuesPayload>> => async (dispatch) => {
	try {
		dispatch({ type: FORM_TEMPLATE_QUEUES.FORM_TEMPLATE_QUEUES_LOAD_START })
		const { data } = await getReq('/api/admin/queues/')

		const payload: any = {
			data: get(data, 'data.queues').map((queue: any) => ({ label: queue.title, value: `${queue.name}` }))
		}

		dispatch({ type: FORM_TEMPLATE_QUEUES.FORM_TEMPLATE_QUEUES_LOAD_DONE, payload })
		return payload
	} catch (e) {
		dispatch({ type: FORM_TEMPLATE_QUEUES.FORM_TEMPLATE_QUEUES_LOAD_FAIL })
		return e
	}
}

export const getDatabases = (): ThunkResult<Promise<IDatabasesPayload>> => async (dispatch) => {
	try {
		dispatch({ type: FORM_TEMPLATE_DATABASES.FORM_TEMPLATE_DATABASES_LOAD_START })
		const { data } = await getReq('/api/admin/databases/')

		const payload: any = {
			data: get(data, 'data.databases').map((databases: string) => ({ label: databases, value: databases }))
		}

		dispatch({ type: FORM_TEMPLATE_DATABASES.FORM_TEMPLATE_DATABASES_LOAD_DONE, payload })
		return payload
	} catch (e) {
		dispatch({ type: FORM_TEMPLATE_DATABASES.FORM_TEMPLATE_DATABASES_LOAD_FAIL })
		return e
	}
}

export const getProcedures =
	(databaseName?: string): ThunkResult<Promise<IProceduresPayload>> =>
	async (dispatch) => {
		try {
			dispatch({ type: FORM_TEMPLATE_PROCEDURES.FORM_TEMPLATE_PROCEDURES_LOAD_START })
			const { data } = await getReq(`/api/admin/databases/${databaseName}/procedures`)

			const payload: any = {
				data: get(data, 'data.storedProcedures').map((databases: string) => ({ label: databases, value: databases }))
			}

			dispatch({ type: FORM_TEMPLATE_PROCEDURES.FORM_TEMPLATE_PROCEDURES_LOAD_DONE, payload })
			return payload
		} catch (e) {
			dispatch({ type: FORM_TEMPLATE_PROCEDURES.FORM_TEMPLATE_PROCEDURES_LOAD_FAIL })
			return e
		}
	}

export const getColumns =
	(id?: string, page?: string): ThunkResult<Promise<IColumnsPayload>> =>
	async (dispatch) => {
		try {
			dispatch({ type: FORM_TEMPLATE_COLUMNS.FORM_TEMPLATE_COLUMNS_LOAD_START })
			let path = `/api/admin/forms/${id}/columns`
			if (page === PAGE.TEMPLATE_BUILDER) {
				path = `/api/admin/templates/${id}/columns`
			}
			const { data } = await getReq(path)

			const payload: any = {
				data: get(data, 'data.columns').map((columns: string) => ({ label: columns, value: columns }))
			}

			dispatch({ type: FORM_TEMPLATE_COLUMNS.FORM_TEMPLATE_COLUMNS_LOAD_DONE, payload })
			return payload
		} catch (e) {
			dispatch({ type: FORM_TEMPLATE_COLUMNS.FORM_TEMPLATE_COLUMNS_LOAD_FAIL })
			return e
		}
	}

export const getElements =
	(id?: string, page?: string): ThunkResult<Promise<IElementsPayload>> =>
	async (dispatch) => {
		try {
			dispatch({ type: FORM_TEMPLATE_ELEMENTS.FORM_TEMPLATE_ELEMENTS_LOAD_START })
			let path = `/api/admin/forms/${id}/validable-components`
			if (page === PAGE.TEMPLATE_BUILDER) {
				path = `/api/admin/templates/${id}/validable-components`
			}
			const { data } = await getReq(path)

			const payload: any = {
				data: data.data.validableComponents
					.map((element: any) => {
						const formItemGeneral = JSON.parse(element.formItemGeneral)
						return {
							label: `${formItemGeneral?.[ELEMENT_ATTRIBUTES.LABEL]} - ${element.type}`,
							value: formItemGeneral?.[ELEMENT_ATTRIBUTES.TABLE_COLUMN]
						}
					})
					.filter((option: IItem) => option.value)
			}

			dispatch({ type: FORM_TEMPLATE_ELEMENTS.FORM_TEMPLATE_ELEMENTS_LOAD_DONE, payload })
			return payload
		} catch (e) {
			dispatch({ type: FORM_TEMPLATE_ELEMENTS.FORM_TEMPLATE_ELEMENTS_LOAD_FAIL })
			return e
		}
	}

export const getFormTabs =
	(id?: string, page?: string): ThunkResult<Promise<ITabsPayload>> =>
	async (dispatch) => {
		try {
			dispatch({ type: FORM_TEMPLATE_TABS.FORM_TEMPLATE_TABS_LOAD_START })
			let path = `/api/admin/forms/${id}/tabs`
			if (page === PAGE.TEMPLATE_BUILDER) {
				path = `/api/admin/templates/${id}/tabs`
			}
			const { data } = await getReq(path)

			const payload: any = {
				data: data.data.tabs.map((tabs: any) => ({ label: tabs.title, value: tabs.i, extra: { parentage: tabs.parentage } }))
			}

			dispatch({ type: FORM_TEMPLATE_TABS.FORM_TEMPLATE_TABS_LOAD_DONE, payload })
			return payload
		} catch (e) {
			dispatch({ type: FORM_TEMPLATE_TABS.FORM_TEMPLATE_TABS_LOAD_FAIL })
			return e
		}
	}

export const getEmailsConfigs = (): ThunkResult<Promise<ITabsPayload>> => async (dispatch) => {
	try {
		dispatch({ type: FORM_TEMPLATE_CONFIGS.FORM_TEMPLATE_CONFIGS_LOAD_START })
		const { data } = await getReq('/api/admin/databases/email-configs')

		const payload: any = {
			data: data.data.configs.map((config: IEmailConfig) => ({ label: config?.name, value: config?.id }))
		}

		dispatch({ type: FORM_TEMPLATE_CONFIGS.FORM_TEMPLATE_CONFIGS_LOAD_DONE, payload })
		return payload
	} catch (e) {
		dispatch({ type: FORM_TEMPLATE_CONFIGS.FORM_TEMPLATE_CONFIGS_LOAD_FAIL })
		return e
	}
}

export const getFormTableItems =
	(id?: string, page?: string): ThunkResult<Promise<IFormItemsPayload>> =>
	async (dispatch) => {
		try {
			dispatch({ type: FORM_TABLE_ITEMS.FORM_TABLE_ITEMS_LOAD_START })
			let path = ''
			if (page === PAGE.TEMPLATE_BUILDER) {
				path = `/api/admin/templates/${id}/form-elements`
			} else {
				path = `/api/admin/forms/${id}/form-elements`
			}
			const { data } = await getReq(path)

			const payload: any = {
				data: {
					formElements: data.data.formElements,
					callButtons: data.data.callButtons
				}
			}
			dispatch({ type: FORM_TABLE_ITEMS.FORM_TABLE_ITEMS_LOAD_DONE, payload })
			return payload
		} catch (e) {
			dispatch({ type: FORM_TABLE_ITEMS.FORM_TABLE_ITEMS_LOAD_FAIL })
			return e
		}
	}

export const saveItemCopy =
	(formItem: IFormItemData): ThunkResult<void> =>
	(dispatch) => {
		const data = { ...formItem }
		dispatch({ type: FORM_TEMPLATE_ITEM_COPY, data })
	}
